import type { ClientErrorReport } from '../dto/client-error.js';

/**
 * SIL-2222: a URL fragment can carry a secret (the referral signup `#code=`,
 * OAuth `#id_token=`). Never send it: drop everything from the first `#`.
 */
export function stripUrlFragment(url: string | undefined): string | undefined {
  if (url === undefined) return undefined;
  const i = url.indexOf('#');
  return i === -1 ? url : url.slice(0, i);
}

/**
 * Best-effort POST of a client-side error to POST /api/client-error
 * (SIL-666).
 *
 * Deliberately uses the platform `fetch`, never an auth-attaching wrapper
 * (e.g. `authFetch`/`createAuthFetch`) — the error being reported may BE a
 * broken auth flow, so this call must not depend on auth working. Never
 * throws: a failed error report must not itself crash the page or mask the
 * original error it's trying to report. `keepalive: true` lets the request
 * survive a navigation/unmount that immediately follows the crash (e.g.
 * React tearing down the tree right after ErrorBoundary catches).
 */
export function reportClientError(
  apiBaseUrl: string,
  report: ClientErrorReport,
  /**
   * SIL-1582: optional synchronous token source so the API can attribute the
   * crash to org/user. Must return the CURRENT stored token or null - never
   * refresh or await (the session may be exactly what is broken). Any throw
   * is swallowed and the report goes out anonymous.
   */
  getToken?: () => string | null,
): void {
  try {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    try {
      const token = getToken?.();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch {
      // Token lookup failed - report anonymously.
    }
    void fetch(`${apiBaseUrl.replace(/\/$/, '')}/api/client-error`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ ...report, url: stripUrlFragment(report.url) }),
      keepalive: true,
    }).catch(() => {
      // Best-effort — nothing further to do if the report itself fails to send.
    });
  } catch {
    // Synchronous throw (e.g. fetch unavailable in this environment) —
    // swallow, never let error reporting itself become a second crash.
  }
}
